import type { InlineToken } from '@/components/block-primitives';
import type { InvestigationSourceDetail } from '../../hooks/use-sources';

export type SourceToken = { kind: 'source'; sequence: number; locators: string[] };

// Asserts, it does not validate: `kind` is the discriminator and a kind's own `parse` guarantees the rest.
export const isSourceToken = (token: InlineToken): token is SourceToken => {
  return token.kind === 'source';
};

// The `[S#]` source-kind grammar and chip styling, shared by both surfaces a source cite renders on:
// the ProseMirror decoration (inline-markup-extension.ts) and the React block path. Reusing these
// helpers keeps the two in lockstep.

// Tail is bounded (no newline, ≤200 chars) so a degenerate unclosed `[S1 …` in model-generated
// artifact text can't trigger O(n²) backtracking on the main thread; real locators are short.
export const SOURCE_CITATION_RE = /\[S\d+(?:\s+[^\]\n]{0,200})?\]/g;
const SOURCE_SEQUENCE_RE = /^\[S(\d+)/;

// Two states only: whether the backend pinned a cite to a specific node is nothing a reader can act
// on, and both open the same popover — which says for itself whether there's a passage. So only a
// cite naming a source that doesn't exist looks different.
export type ChipState = 'missing' | 'plain';

const SOURCE_LABEL_RE = /^S(\d+)$/;

/** The `10` in an `S10` source label, or null for a label the backend didn't shape that way. */
export const labelSequence = (label: string) => {
  const sequence = Number(label.match(SOURCE_LABEL_RE)?.[1]);
  return Number.isFinite(sequence) ? sequence : null;
};

// A `[S#]` cite carries only the sequence, so every surface that renders one — prose decorations,
// block chips, the popover — needs this same lookup off the sources response.
export const sourcesBySequence = (sources: InvestigationSourceDetail[] = []) => {
  const bySequence: Record<number, InvestigationSourceDetail> = {};
  for (const source of sources) {
    const sequence = labelSequence(source.label);
    if (sequence !== null) bySequence[sequence] = source;
  }
  return bySequence;
};

// The one `S#` badge, rendered by a prose cite, the popover header and a sources-dialog row alike.
// The explicit height is what keeps those the same size: as a flex *item* the badge's `inline-flex`
// blockifies to `flex` and loses the line-height strut, so padding alone leaves it ~2px shorter than
// the same badge sitting inline in prose.
export const SOURCE_BADGE_CLASS =
  'inline-flex h-[22px] items-center rounded-default border border-tertiary bg-secondary px-1.5 font-mono text-[13px] leading-4';

// Only the prose cite is interactive, so only it takes a hover — a cite that stays inert on hover
// reads as decoration rather than as something to open.
const CHIP_BASE = `source-citation-chip ${SOURCE_BADGE_CLASS} align-baseline cursor-default transition-colors hover:bg-hover-control focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-accent`;

export const chipClass = (state: ChipState) =>
  state === 'missing' ? `${CHIP_BASE} text-tertiary` : `${CHIP_BASE} text-accent`;

// Percy often writes a bare cite beside a pinpointed one (`[S1], [S1 /paragraphs/12]`), which would
// render as two identical `S1` chips. Consecutive cites of the *same* source therefore collapse into
// one chip carrying every locator, and the popover pages through their passages.
export type CitationRun = {
  /** Offset of the run's first marker within the text node. */
  index: number;
  /** The run's full source text, separators included — hidden wholesale by the decoration pass. */
  raw: string;
  sequence: number;
  /** Locators cited for this source across the run, in document order. Bare cites contribute none. */
  locators: string[];
  markerCount: number;
};

// Only same-line whitespace and list punctuation may sit between merged cites: `[S1], [S1 /x]`
// merges, `[S1] and [S1 /x]` doesn't, because dropping the "and" would rewrite the sentence. A
// newline is excluded too — the merged chip swallows the separator, which would join two lines.
const RUN_SEPARATOR_RE = /^[ \t,;]*$/;

const continuesRun = (text: string, previous: CitationRun, sequence: number, index: number) =>
  previous.sequence === sequence && RUN_SEPARATOR_RE.test(text.slice(previous.index + previous.raw.length, index));

export const collectCitationRuns = (text: string) => {
  const runs: CitationRun[] = [];
  for (const match of text.matchAll(SOURCE_CITATION_RE)) {
    if (match.index === undefined) continue;
    const parsed = matchSequence(match[0]);
    if (!parsed) continue;
    const previous = runs.at(-1);
    if (previous && continuesRun(text, previous, parsed.sequence, match.index)) {
      previous.raw = text.slice(previous.index, match.index + match[0].length);
      previous.markerCount += 1;
      if (parsed.locator) previous.locators.push(parsed.locator);
      continue;
    }
    runs.push({
      index: match.index,
      raw: match[0],
      sequence: parsed.sequence,
      locators: parsed.locator ? [parsed.locator] : [],
      markerCount: 1,
    });
  }
  return runs;
};

// Locators ride on the chip as JSON: a locator is itself a slash-separated pointer, so a
// delimiter-joined string couldn't be split back apart.
export const serializeLocators = (locators: string[]) => (locators.length ? JSON.stringify(locators) : undefined);

export const parseLocators = (value: string | null | undefined) => {
  if (!value) return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed.filter((entry): entry is string => typeof entry === 'string') : [];
  } catch {
    return [];
  }
};

// Splits a `[S10 /pointer]` marker into its sequence and optional locator. Returns null when the
// marker isn't a well-formed `[S<number>…]`.
export const matchSequence = (marker: string) => {
  const label = marker.match(SOURCE_SEQUENCE_RE); // `[S10`
  const sequence = Number(label?.[1]);
  if (!label || !Number.isFinite(sequence)) return null;
  const locator = marker.slice(label[0].length, -1).trim() || null; // between `[S10` and `]`
  return { sequence, locator };
};

// The chip's accessible label uses the source's common `title` (present on every source kind), so it
// needs no per-kind handling and works for any source type the backend introduces.
export const buildHoverLabel = (sequence: number, source: InvestigationSourceDetail | undefined) =>
  source ? `S${sequence}: ${source.title}` : `S${sequence}: source not found`;
